import { useCallback, useEffect, useRef, useState } from 'react';

import { useImageSearch } from '@/hooks/useImageSearch';
import { useIsMobileLayout } from '@/hooks/useIsMobileLayout';
import { useOneTimePopoverWasDismissed } from '@/hooks/useOneTimePopoverWasDismissed';
import { useTrackSearchByImageClickedAnalytics } from '@/utils/amplitude/analytics/searchByImage/trackSearchByImageClicked.analytics';

import { SearchByImageBottomSheet } from '../components/SearchByImageBottomSheet';
import { SearchByImageEducationPopover } from '../components/SearchByImageEducationPopover';
import { SearchByImagePopover } from '../components/SearchByImagePopover';

const SEARCH_BY_IMAGE_EDUCATION_COOKIE = 'searchByImageEducationPopoverDismissed';

/**
 * Shared open/close wiring for the Search by Image triggers (treatmenta beside the input,
 * treatmentb inside it). Owns the popover (desktop) / bottom sheet (mobile) surface, the
 * desktop outside-click + Escape dismissal, and the open-time tracking + stale-error clear,
 * so both triggers reuse one implementation instead of duplicating the entry-point body.
 *
 * Attach `containerRef` to the wrapper that holds both the trigger and `surface`, so the
 * outside-click check treats clicks on the open popover as inside. Exposure is NOT resolved
 * here — HammerHeader mounts the experiment hook once so it fires for every arm incl. control;
 * a trigger only renders for its own arm.
 */
export const useSearchByImageSurface = () => {
    const isMobile = useIsMobileLayout();
    const { clearError, status } = useImageSearch();
    const trackSearchByImageClicked = useTrackSearchByImageClickedAnalytics();
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    // A trigger only mounts for an active treatment arm, so first mount = first exposure; the
    // cookie makes the nudge one-time per device. Control never renders a trigger, so this hook
    // never runs there and the popover is structurally absent on control.
    const { handleDismissPopover: dismissEducation, showPopover: showEducation } = useOneTimePopoverWasDismissed(
        SEARCH_BY_IMAGE_EDUCATION_COOKIE,
        true
    );

    useEffect(() => {
        // The mobile bottom sheet portals out of this container and manages its own
        // dismissal (overlay tap / drag / Escape), so the outside-click + Escape
        // handling below is only for the desktop anchored popover.
        if (!isOpen || isMobile) {
            return;
        }
        const handleDocumentClick = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsOpen(false);
            }
        };
        document.addEventListener('click', handleDocumentClick);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('click', handleDocumentClick);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, isMobile]);

    const close = useCallback(() => {
        setIsOpen(false);
    }, []);

    const onTriggerClick = useCallback(() => {
        if (!isOpen) {
            // Clear a stale error so the surface reopens on a clean dropzone. This drops
            // only the error (not the results), so reopening over a results page keeps
            // those results and never redirects home.
            if (status === 'error') {
                clearError();
            }
            // Opening the uploader means the education nudge did its job — dismiss it for
            // good so it never stacks over the popover/sheet or returns next session.
            if (showEducation) {
                dismissEducation();
            }
            trackSearchByImageClicked();
        }
        setIsOpen((open) => !open);
    }, [clearError, dismissEducation, isOpen, showEducation, status, trackSearchByImageClicked]);

    const surface = isMobile ? (
        <SearchByImageBottomSheet
            onClose={close}
            open={isOpen}
        />
    ) : (
        isOpen && <SearchByImagePopover onClose={close} />
    );

    const education = showEducation ? (
        // Key on the layout so the popover remounts when the trigger swaps between the labelled
        // button (tablet+) and the compact icon (mobile). Without it, react-tooltip anchors to the
        // old element and, when React replaces it, drops the anchor and hides the popover for good
        // (it can't re-open against a constant `isOpen`). This bites on an SSR/client layout
        // mismatch and on a desktop→mobile resize during first exposure.
        <SearchByImageEducationPopover
            key={isMobile ? 'mobile' : 'desktop'}
            isMobile={isMobile}
            onDismiss={dismissEducation}
        />
    ) : null;

    return {
        containerRef,
        education,
        isMobile,
        isOpen,
        onTriggerClick,
        surface,
    };
};
